/**
 * Conversation Store — Named Selectors
 *
 * Provides referentially stable selector functions for useConversationStore.
 * Import these instead of declaring inline `(s) => s.foo` selectors in screens
 * to avoid unnecessary re-renders and reduce duplication.
 *
 * NOTE: ConversationState is not exported from conversationStore.ts, so we
 * derive the type via ReturnType<typeof useConversationStore.getState>.
 */

import { useConversationStore } from './conversationStore';

type ConversationState = ReturnType<typeof useConversationStore.getState>;

// ── Session ──────────────────────────────────────────────────────────

export const selectCurrentSessionId = (s: ConversationState) => s.currentSessionId;
export const selectFlowType = (s: ConversationState) => s.flowType;
export const selectHasAnalysis = (s: ConversationState) => s.hasAnalysis;
export const selectHasOpening = (s: ConversationState) => s.hasOpening;

// ── Messages ─────────────────────────────────────────────────────────

export const selectMessages = (s: ConversationState) => s.messages;

// ── UI State ─────────────────────────────────────────────────────────

export const selectIsSendingMessage = (s: ConversationState) => s.isSendingMessage;
export const selectIsRecording = (s: ConversationState) => s.isRecording;
export const selectIsStreaming = (s: ConversationState) => s.isStreaming;
export const selectIsAnalyzing = (s: ConversationState) => s.isAnalyzing;
export const selectConversationError = (s: ConversationState) => s.error;
export const selectErrorInfo = (s: ConversationState) => s.errorInfo;

// ── Multi-device ─────────────────────────────────────────────────────

export const selectPeerTyping = (s: ConversationState) => s.peerTyping;

// ── Streaming isolation ──────────────────────────────────────────────

export const selectStreamingText = (s: ConversationState) => s.streamingText;
export const selectStreamingMessageId = (s: ConversationState) => s.streamingMessageId;

// ── Actions: Session lifecycle ───────────────────────────────────────

export const selectStartNewSession = (s: ConversationState) => s.startNewSession;
export const selectLoadSession = (s: ConversationState) => s.loadSession;
export const selectTriggerOpening = (s: ConversationState) => s.triggerOpening;

// ── Actions: Text messages ───────────────────────────────────────────

export const selectSendTextMessage = (s: ConversationState) => s.sendTextMessage;
export const selectSendTextMessageStream = (s: ConversationState) => s.sendTextMessageStream;

// ── Actions: Voice messages ──────────────────────────────────────────

export const selectStartRecording = (s: ConversationState) => s.startRecording;
export const selectStopRecording = (s: ConversationState) => s.stopRecording;
export const selectStopRecordingStream = (s: ConversationState) => s.stopRecordingStream;
export const selectCancelRecording = (s: ConversationState) => s.cancelRecording;

// ── Actions: Retry ───────────────────────────────────────────────────

export const selectRetryConversation = (s: ConversationState) => s.retryConversation;
export const selectRetryAnalysis = (s: ConversationState) => s.retryAnalysis;

// ── Actions: UI ──────────────────────────────────────────────────────

export const selectToggleAnalysisExpanded = (s: ConversationState) => s.toggleAnalysisExpanded;

// ── Actions: Cleanup ─────────────────────────────────────────────────

export const selectClearError = (s: ConversationState) => s.clearError;
export const selectClearMessages = (s: ConversationState) => s.clearMessages;
export const selectLeaveCurrentSession = (s: ConversationState) => s.leaveCurrentSession;
